import { schema } from "normalizr";

import { NImage } from "@files/models";
import { NLabel } from "@music/models";

import { TranslatableMap } from "./localisation";
import { Organisation, NOrganisation } from "./auth";
import { ApiModel } from "./model";
import { Image } from "./files";

interface Album extends ApiModel {
    kind?: "album";
    title: string;
    title_version: string;
    upc: string;
    format_name: string;
    status_name: string;
    custom_id: string;
    genre_id: number;
    additional_genre_id: number;
    label_id: number;
    artwork_id: number;
    copyright_artwork: string;
    copyright_artwork_year: number;
    copyright_sound: string;
    copyright_sound_year: number;
    original_release_date: Date;
    preorder_date: Date;
    release_date: Date;
    preorder_preview: boolean;
    comment: string;
    comment_private: boolean;
    ready_for_delivery: boolean;
    organisation_id: number;
    parent_id: number;
    created_at: Date;
    updated_at: Date;
    deleted_at: Date;
    // Relationships
    artists?: Artist[] | number[];
    tracks?: Track[] | number[];
    artwork?: Image | number;
    format?: AlbumFormat | string;
    status?: AlbumStatus | string;
    label?: Label | number;
    genre?: Genre | number;
    additional_genre?: Genre | number;
    organisation?: Organisation | number;
}

interface Track extends ApiModel {
    kind: "track";
    title: string;
    title_version: string;
    isrc: string;
    genre_id: number;
    label_id: number;
    explicit: boolean;
    gapless: boolean;
    ready_for_delivery: boolean;
    right_available: boolean;
    copyright_sound: string;
    copyright_sound_year: number;
    duration: number;
    organisation_id: number;
    created_at: Date;
    updated_at: Date;
    deleted_at: Date;
    artists?: Artist[] | number[];
    pivot?: AlbumTrack | string;
}

interface Artist extends ApiModel {
    kind: "artist";
    name: string;
    publish: boolean;
    image?: string;
    created_at: Date;
    updated_at: Date;
    deleted_at: Date;
    pivot?: ArtistPivot | string;
    albums?: Album[] | number[];
}

interface Genre extends ApiModel {
    kind: "genre";
    name: string;
    label: TranslatableMap;
    created_at: Date;
    updated_at: Date;
    deleted_at: Date;
}

interface Label extends ApiModel {
    kind?: "label";
    name: string;
}

interface AlbumFormat {
    name: string;
    label: TranslatableMap;
}

interface AlbumStatus {
    name: string;
    label: TranslatableMap;
}

// Pivot models
interface AlbumTrack {
    album_id: number;
    track_id: number;
    disc: number;
    track: number;
    album_only: boolean;
    preorder_date: Date;
    release_date: Date;
}

interface ArtistPivot {
    artist_id: number;
    role_name: string;
    is_main: boolean;
    order: number;
}

interface AlbumArtist extends ArtistPivot {
    album_id: number;
}

interface TrackArtist extends ArtistPivot {
    track_id: number;
}

function isAlbumArtistPivot(val: ArtistPivot): val is AlbumArtist {
    return (val as AlbumArtist).album_id !== undefined;
}

function isTrackArtistPivot(val: ArtistPivot): val is TrackArtist {
    return (val as TrackArtist).track_id !== undefined;
}

// Define the normalised version of the entities.
const NAlbumFormat = new schema.Entity("albumFormats", {}, {
    idAttribute: "name",
});
const NAlbumStatus = new schema.Entity("albumStatuses", {}, {
    idAttribute: "name",
});
const NAlbumTrack = new schema.Entity("albumTracks", {}, {
    idAttribute: (value: AlbumTrack) => {
        return value.album_id + "_" + value.track_id;
    },
});
const NArtistPivot = new schema.Entity("artistPivots", {}, {
    idAttribute: (value: ArtistPivot) => {
        if (isAlbumArtistPivot(value)) {
            return "album_" + value.album_id + "_" + value.artist_id + "_" + value.role_name + "_" + value.order;
        }
        if (isTrackArtistPivot(value)) {
            return "track_" + value.track_id + "_" + value.artist_id + "_" + value.role_name + "_" + value.order;
        }
        return value.artist_id + "_" + value.role_name;
    },
});
const NGenre = new schema.Entity("genres");
const NArtist = new schema.Entity("artists");
// TODO Need a custom merge strategy to ensure we keep relationships when newer hasn't loaded them.
const NTrack = new schema.Entity("tracks");
const NAlbum = new schema.Entity("albums", {
    artists: [ NArtist ],
    tracks: [ NTrack ],
    artwork: NImage,
    format: NAlbumFormat,
    status: NAlbumStatus,
    label: NLabel,
    genre: NGenre,
    additional_genre: NGenre,
    organisation: NOrganisation,
    pivot: NAlbumTrack,
});
NTrack.define({
    artists: [ NArtist ],
    albums: [ NAlbum ],
    genre: NGenre,
    pivot: NAlbumTrack,
});
NArtist.define({
    pivot: NArtistPivot,
    albums: [ NAlbum ],
});

interface MusicNormalisedEntities {
    albums?: {
        [key: string]: Album;
    };
    tracks?: {
        [key: string]: Track;
    };
    artists?: {
        [key: number]: Artist;
    };
    genres?: {
        [key: number]: Genre;
    };
    labels?: {
        [key: number]: Label;
    };
    albumFormats?: {
        [key: string]: AlbumFormat;
    };
    albumStatuses?: {
        [key: string]: AlbumStatus;
    };
    albumTracks?: {
        [key: string]: AlbumTrack;
    };
    artistPivots?: {
        [key: string]: ArtistPivot;
    };
}

export {
    Album,
    Track,
    Artist,
    Genre,
    Label,
    AlbumFormat,
    AlbumStatus,
    AlbumTrack,
    ArtistPivot,
    NAlbum,
    NTrack,
    NArtist,
    NGenre,
    NLabel,
    NAlbumFormat,
    NAlbumStatus,
    NAlbumTrack,
    NArtistPivot,
    MusicNormalisedEntities,
};
